import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Form,
  Button,
  Card,
  Row,
  Col,
  Table,
  ProgressBar,
  Badge,
  Alert,
} from "react-bootstrap";
import Navbar from "./Navbar";
import { start } from "@popperjs/core";
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';
import useNavigationWarning from './hooks/useNavigationWarning';
import NavigationWarningModal from './components/NavigationWarningModal';
import { getRatingOptions } from "./utils/ratingScales";

const ResidentEvaluationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [activeEntryIndex, setActiveEntryIndex] = useState(0);
  const [bookmarks, setBookmarks] = useState(new Set());
  const [isStarted, setIsStarted] = useState(false);
  const [evaluationData, setEvaluationData] = useState(null);

  // Save progress function
  const handleSaveProgress = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_ENDPOINTS.RESIDENT}/save-evaluation-progress/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            answers,
            timeLeft,
            timerRunning,
            bookmarks: Array.from(bookmarks),
          }),
        }
      );

      if (res.ok) {
        return true; // Return success for navigation warning
      } else {
        const error = await res.json();
        throw new Error(error.message || "Error saving progress.");
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  // Save timer state only (for when leaving without saving answers)
  const handleSaveTimerOnly = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // First, get the current saved progress to preserve existing answers
      const getCurrentProgress = await fetch(
        `${API_ENDPOINTS.RESIDENT}/evaluation/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      let existingAnswers = {};
      let existingBookmarks = [];
      
      if (getCurrentProgress.ok) {
        const currentData = await getCurrentProgress.json();
        existingAnswers = currentData.savedProgress?.answers || {};
        existingBookmarks = currentData.savedProgress?.bookmarks || [];
      }
      
      // Merge current bookmarks with existing ones (use current state if available, otherwise use existing)
      const bookmarksToSave = bookmarks.size > 0 ? Array.from(bookmarks) : existingBookmarks;
      
      const res = await fetch(
        `${API_ENDPOINTS.RESIDENT}/save-evaluation-progress/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            answers: existingAnswers, // Preserve existing answers
            timeLeft,
            timerRunning,
            bookmarks: bookmarksToSave,
          }),
        }
      );

      if (res.ok) {
        return true;
      } else {
        const error = await res.json();
        throw new Error(error.message || "Error saving timer state.");
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  // Navigation warning system - only active when evaluation has been started
  const {
    showWarningModal,
    handleSaveAndLeave,
    handleLeaveWithoutSaving,
    handleCancelNavigation,
    isTimed,
    timeLeft: warningTimeLeft
  } = useNavigationWarning({
    isActive: isStarted, // Only active when evaluation is started
    evaluationData: { answers, timeLeft },
    onSaveProgress: handleSaveProgress,
    isTimed: template?.isTimed || false,
    onSaveTimer: handleSaveTimerOnly // Separate function for saving timer state only
  });

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_ENDPOINTS.RESIDENT}/submit-evaluation/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ answers }),
        }
      );

      if (res.ok) {
        alert("Evaluation submitted successfully!");
        navigate("/resident/evaluation");
      } else {
        const error = await res.json();
        alert(error.message || "Error submitting evaluation.");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to submit evaluation");
    }
  };

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_ENDPOINTS.RESIDENT}/evaluation/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!res.ok) {
          const errorText = await res.text();
          console.error("API Error Response:", {
            status: res.status,
            statusText: res.statusText,
            body: errorText
          });
          throw new Error(`API Error: ${res.status} ${res.statusText}`);
        }
        
        const data = await res.json();
        console.log("Fetched template data:", data); // Debug log

        setTemplate(data.template);
        setEvaluationData(data);
        
        // Always start with isStarted = false to show start screen first
        // User must click "Continue Evaluation" or "Start Evaluation" to proceed
        setIsStarted(false);
        setQuestions(
          data.questions.map((q) => {
            if (q.questionType === "header-paragraph") {
              try {
                const parsed = JSON.parse(q.questionText);
                return {
                  ...q,
                  type: q.questionType,
                  header: parsed.header,
                  paragraph: parsed.paragraph,
                  size: parsed.size,
                };
              } catch (e) {
                console.error("Error parsing header-paragraph:", e);
                return {
                  ...q,
                  type: q.questionType,
                  header: q.questionText,
                  paragraph: "",
                  size: "h2",
                };
              }
            }
            if (q.questionType === "radio-matrix") {
              return {
                ...q,
                type: q.questionType,
                label: q.questionText,
                options: q.options || [],
                columns:
                  q.columns && q.columns.length > 0
                    ? q.columns
                    : [
                        "Unsatisfactory",
                        "Needs Improvement",
                        "Meets Expectations",
                        "Exceeds Expectations",
                      ],
              };
            }
            return {
              ...q,
              type: q.questionType,
              label: q.questionText,
              options: q.options || [],
            };
          })
        );
        setActiveEntryIndex(0);

        // Initialize timer if evaluation is timed (but don't start it until evaluation begins)
        if (data.template.isTimed) {
          console.log(
            "Initializing timer with time limit:",
            data.template.timeLimit
          ); // Debug log
          // If there's saved progress, use that time
          if (
            data.savedProgress &&
            typeof data.savedProgress.timeLeft === "number"
          ) {
            console.log("Using saved time:", data.savedProgress.timeLeft);
            setTimeLeft(data.savedProgress.timeLeft);
            setTimerRunning(false); // Don't start timer until user clicks continue
          } else {
            console.log("Initializing new timer (not started yet)");
            setTimeLeft(data.template.timeLimit * 60); // Convert minutes to seconds
            setTimerRunning(false); // Don't start timer until evaluation begins
          }
        }
        // Restore saved answers for all question types
        if (data.savedProgress && data.savedProgress.answers) {
          setAnswers(data.savedProgress.answers);
        }
        // Restore bookmarks if they exist
        if (data.savedProgress && data.savedProgress.bookmarks && Array.isArray(data.savedProgress.bookmarks)) {
          setBookmarks(new Set(data.savedProgress.bookmarks));
        }
      } catch (error) {
        console.error("Error fetching template:", error);
      }
    };

    fetchTemplate();
  }, [id]);

  // Timer effect
  useEffect(() => {
    let interval;
    if (timerRunning && timeLeft > 0) {
      console.log("Starting timer with time left:", timeLeft); // Debug log
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setTimerRunning(false);
            handleSubmit(); // Auto-submit when time runs out
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timerRunning, timeLeft, handleSubmit]);

  const handleSaveProgressAndNavigate = async () => {
    try {
      await handleSaveProgress();
      alert("Progress saved!");
      navigate("/resident/evaluation");
    } catch (err) {
      console.error(err);
      alert("Failed to save progress");
    }
  };

  // Helper: get all numbered questions (exclude header-paragraph and divider)
  const numberedQuestions = questions.filter(
    (q) => !["header-paragraph", "divider"].includes(q.type)
  );
  const questionMap = useMemo(() => {
    const map = {};
    numberedQuestions.forEach((q, idx) => {
      map[q.id] = { question: q, index: idx };
    });
    return map;
  }, [numberedQuestions]);
  const sections = useMemo(() => {
    const list = [];
    let currentSectionBlock = null;
    let fallbackIndex = 1;

    const startFallbackSection = () => {
      currentSectionBlock = {
        id: `section-${fallbackIndex}`,
        header: `Section ${fallbackIndex}`,
        paragraph: "",
        questionIds: [],
        hasOverviewContent: false,
      };
      fallbackIndex += 1;
      list.push(currentSectionBlock);
    };

    questions.forEach((question) => {
      if (question.type === "header-paragraph") {
        const headerContent = question.header || "";
        const paragraphContent = question.paragraph || "";
        currentSectionBlock = {
          id: question.id || `section-${fallbackIndex}`,
          header: headerContent || `Section ${fallbackIndex}`,
          paragraph: paragraphContent || "",
          questionIds: [],
          hasOverviewContent:
            Boolean(headerContent && headerContent.trim()) ||
            Boolean(paragraphContent && paragraphContent.trim()),
        };
        fallbackIndex += 1;
        list.push(currentSectionBlock);
        return;
      }
      if (question.type === "divider") {
        return;
      }
      if (!currentSectionBlock) {
        startFallbackSection();
      }
      currentSectionBlock.questionIds.push(question.id);
    });

    return list;
  }, [questions]);
  const { navigationEntries, sectionEntryIndexMap } = useMemo(() => {
    const entries = [];
    const sectionIndexMap = {};

    // Add top-level introduction entry if introHtml exists
    if (template?.introHtml && template.introHtml.trim()) {
      entries.push({
        type: "intro",
        id: "intro",
        sectionIndex: null,
      });
    }

    sections.forEach((section, idx) => {
      const sectionKey = section.id || `section-${idx + 1}`;
      if (section.hasOverviewContent) {
        const overviewIndex = entries.length;
        entries.push({
          type: "overview",
          id: `overview-${sectionKey}`,
          sectionIndex: idx,
          header: section.header,
          paragraph: section.paragraph,
        });
        sectionIndexMap[sectionKey] = overviewIndex;
      }

      section.questionIds.forEach((questionId) => {
        const mapEntry = questionMap[questionId];
        if (mapEntry) {
          entries.push({
            type: "question",
            id: questionId,
            sectionIndex: idx,
            question: mapEntry.question,
            questionNumber: mapEntry.index + 1,
          });
        }
      });
    });

    return {
      navigationEntries: entries,
      sectionEntryIndexMap: sectionIndexMap,
    };
  }, [sections, questionMap]);

  useEffect(() => {
    if (navigationEntries.length === 0) {
      setActiveEntryIndex(0);
      return;
    }
    if (activeEntryIndex > navigationEntries.length - 1) {
      setActiveEntryIndex(navigationEntries.length - 1);
    }
  }, [navigationEntries, activeEntryIndex]);
  // Helper: check if a question is filled out
  const isQuestionFilled = (q) => {
    if (q.type === "checkbox") {
      return Array.isArray(answers[q.id]) && answers[q.id].length > 0;
    }
    if (q.type === "radio-matrix") {
      return (
        answers[q.id] && Object.keys(answers[q.id]).length === q.options.length
      );
    }
    return (
      answers[q.id] !== undefined &&
      answers[q.id] !== "" &&
      answers[q.id] !== null
    );
  };
  const isSectionCompleted = (section) =>
    section.questionIds.length > 0 &&
    section.questionIds.every((questionId) => {
      const questionEntry = questionMap[questionId];
      return questionEntry ? isQuestionFilled(questionEntry.question) : false;
    });

  // Progress calculation
  const totalQuestions = numberedQuestions.length;
  const filledQuestions = numberedQuestions.filter(isQuestionFilled).length;
  const progressPercent =
    totalQuestions > 0
      ? Math.round((filledQuestions / totalQuestions) * 100)
      : 0;
  const hasQuestions = totalQuestions > 0;

  const activeEntry = navigationEntries[activeEntryIndex] || null;
  const currentQuestion =
    activeEntry?.type === "question" ? activeEntry.question : null;
  const displayQuestionNumber =
    activeEntry?.type === "question" ? activeEntry.questionNumber : 0;
  const activeSectionIndex =
    typeof activeEntry?.sectionIndex === "number"
      ? activeEntry.sectionIndex
      : sections.length > 0
      ? 0
      : -1;
  const isIntroActive = activeEntry?.type === "intro";

  // Handler functions - defined before early returns
  const handleStartEvaluation = () => {
    setIsStarted(true);
    // Start the timer when evaluation begins
    if (template?.isTimed) {
      setTimerRunning(true);
    }
  };

  const handleNextEntry = () => {
    if (!navigationEntries.length) return;
    setActiveEntryIndex((prev) =>
      Math.min(prev + 1, navigationEntries.length - 1)
    );
  };

  const handlePreviousEntry = () => {
    if (!navigationEntries.length) return;
    setActiveEntryIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSectionClick = (section, sectionIdx) => {
    if (!section) return;
    const sectionKey = section.id || `section-${sectionIdx + 1}`;
    const targetIndex = sectionEntryIndexMap[sectionKey];
    if (typeof targetIndex === "number" && targetIndex >= 0) {
      setActiveEntryIndex(targetIndex);
    }
  };

  const handleToggleBookmark = (questionId) => {
    setBookmarks(prev => {
      const newBookmarks = new Set(prev);
      if (newBookmarks.has(questionId)) {
        newBookmarks.delete(questionId);
      } else {
        newBookmarks.add(questionId);
      }
      return newBookmarks;
    });
  };

  if (!template) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center text-muted">Loading evaluation...</div>
      </div>
    );
  }

  // Show start screen if evaluation hasn't been started
  if (!isStarted) {
    const hasProgress = evaluationData?.savedProgress && (
      (evaluationData.savedProgress.answers && Object.keys(evaluationData.savedProgress.answers).length > 0) ||
      (evaluationData.savedProgress.timeLeft !== null && evaluationData.savedProgress.timeLeft !== undefined)
    );
    const totalQuestionsStart = questions.filter(q => !["header-paragraph", "divider"].includes(q.type)).length;
    const answeredCountStart = hasProgress && evaluationData?.savedProgress?.answers
      ? Object.keys(evaluationData.savedProgress.answers).filter((k) => {
          const a = evaluationData.savedProgress.answers[k];
          return a !== undefined && a !== "" && a !== null && (Array.isArray(a) ? a.length > 0 : true);
        }).length
      : 0;
    const progressPercentStart = totalQuestionsStart > 0 ? Math.round((answeredCountStart / totalQuestionsStart) * 100) : 0;
    const timeDisplay = template.isTimed && timeLeft > 0
      ? `${Math.floor(timeLeft / 60).toString().padStart(2, "0")}:${(timeLeft % 60).toString().padStart(2, "0")}`
      : template.isTimed && template.timeLimit
        ? `${template.timeLimit.toString().padStart(2, "0")}:00`
        : null;

    return (
      <div>
        <PageTitle title="Start Evaluation" />
        <Navbar />
        <Container fluid className=" py-4 d-flex align-items-center justify-content-center">
          <Row className="justify-content-center">
            <Col lg={10} md={10} xs={12}>
          {/* Main card: progress, details, alert */}
          <Card className="border-0 shadow-sm rounded-3 mb-3">
            <Card.Header className="px-4 pt-4 d-flex border-0 ">
              <div className="d-flex flex-column align-items-start justify-content-start">
                         
                <Badge
                  bg={template.evaluationType === "survey" ? "info" : template.evaluationType === "performance" ? "primary" : template.evaluationType === "feedback" ? "info" : "secondary"}
                >
                  {template.evaluationType === "performance"
                    ? "Performance"
                    : template.evaluationType === "feedback"
                      ? "Feedback"
                      : template.evaluationType === "survey"
                        ? "Survey"
                        : template.evaluationType === "questionnaire"
                          ? "Questionnaire"
                          : template.evaluationType || "Evaluation"}
                </Badge>
                <h4 className="mb-0 fw-bold">{template.title}</h4>
              </div>

            </Card.Header>
            <Card.Body className="px-md-4 pb-5">
              {template.description && template.description.trim() && (
                <div className="mb-4">
                <span className="text-muted fw-semibold small">Description:</span>
                <p className="mb-0">{template.description}</p>
                </div>
              )}
              <hr className="my-4" />

              {/* Evaluator, Due Date, Time Remaining */}
              <Row className="g-3 mb-4">
                <Col md={4}>
                  <div className="d-flex align-items-start gap-3">
                    <div className="rounded-2 bg-light p-2 text-muted">
                      <i className="bi bi-person" style={{ fontSize: "1.1rem" }} />
                    </div>
                    <div>
                      <div className="small text-muted fw-semibold">Evaluator</div>
                      <div className="fw-semibold text-dark">{template.facultyName || "Program Director"}</div>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex align-items-start gap-3">
                    <div className="rounded-2 bg-light p-2 text-muted">
                      <i className="bi bi-calendar-event" style={{ fontSize: "1.1rem" }} />
                    </div>
                    <div>
                      <div className="small text-muted fw-semibold">Due Date</div>
                      <div className="fw-semibold text-dark">
                        {template.endTime ? new Date(template.endTime).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "Date to be announced"}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex align-items-start gap-3">
                    <div className="rounded-2 bg-light p-2 text-muted">
                      <i className="bi bi-clock" style={{ fontSize: "1.1rem" }} />
                    </div>
                    <div>
                      <div className="small text-muted fw-semibold">Time Remaining</div>
                      <div className="fw-semibold text-dark">
                        {timeDisplay != null ? timeDisplay : "Untimed"}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
              {/* Progress */}
              <div className="mb-4 bg-light rounded-3 px-4 py-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold text-dark">Progress</div>
                  <div className="text-muted small">
                    {progressPercentStart}% Complete, {answeredCountStart} of {totalQuestionsStart} questions answered
                  </div>
                </div>
                <ProgressBar
                  now={progressPercentStart}
                  variant="success-solid"
                  style={{ height: 6 }}
                  className="rounded-pill"
                />
              </div>
                </Col>
                </Row>
              {/* Auto-save info alert */}
              <Alert variant="info" className="mb-0 d-flex align-items-start gap-2 rounded-3 border-0" style={{ backgroundColor: "var(--bs-info-bg-subtle)" }}>
                <i className="bi bi-info-circle-fill text-info mt-1" style={{ fontSize: "1.1rem" }} />
                <div>
                  <span className="fw-semibold text-info">You need to save your progress before exiting.</span>
                  <span className="text-info"> You can save your progress and exit anytime before the due date.</span>
                </div>
              </Alert>
            </Card.Body>
          </Card>
          <div className="d-flex justify-content-end gap-3">
          <Button variant="light-solid" size="sm" onClick={() => navigate("/resident/evaluation")}>
                <i className="bi bi-arrow-left me-2"></i>
                Back to Evaluations
              </Button>
          <Button
                variant="primary"
                size="sm"
                onClick={handleStartEvaluation}
                className="rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-2"
              >
                <i className={`bi ${hasProgress ? "bi-arrow-clockwise" : "bi-send"} fs-5`} />
                {hasProgress ? "Continue Evaluation" : "Start Evaluation"}
              </Button>
              </div>
              </Col>
              </Row>
        </Container>
      </div>
    );
  }

  const activeSectionMeta =
    activeSectionIndex >= 0 ? sections[activeSectionIndex] : sections[0] || null;
  const activeSectionTitle = activeSectionMeta?.header || template.title;
  const activeSectionParagraph = activeSectionMeta?.paragraph || "";
  const isOverviewActive = activeEntry?.type === "overview";
  const totalEntries = navigationEntries.length;
  const allQuestionsAnswered =
    totalQuestions > 0 &&
    numberedQuestions.every((q) => isQuestionFilled(q));


  const renderQuestion = (q, questionNumber) => {
    if (!q) {
      return (
        <div className="text-muted py-4">
          No questions available for this section.
        </div>
      );
    }

    return (
      <Form.Group className="mb-4">
          <div className="d-flex flex-column align-items-start justify-content-start gap-3 mb-3" >
            <div className="d-flex flex-row align-items-center justify-content-between w-100">
              <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                <span className=" bg-secondary fw-semibold px-3 py-2 rounded-3 small">Question {questionNumber}</span>
                <Button 
                  variant="light" 
                  size="sm" 
                  className="px-2 py-1"
                  onClick={() => handleToggleBookmark(q.id)}
                  style={{ color: bookmarks.has(q.id) ? 'var(--bs-danger)' : 'inherit' }}
                >
                  <i className={`bi ${bookmarks.has(q.id) ? 'bi-bookmark-fill' : 'bi-bookmark'}`}></i>
                </Button>
              </div>
              <span className="text-muted small">{hasQuestions
                              ? `${displayQuestionNumber} of ${totalQuestions}`
                              : "No questions"}</span>
            </div>
            {(q.showInfo || (q.infoHtml && String(q.infoHtml).trim())) && q.infoPlacement === "before" && (
              <div className="mb-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: q.infoHtml || "" }} />
            )}
            <h5 className="fw-semibold mb-0">{q.label || q.questionText}</h5>
          </div>

        {q.type === "text" && (
          <Row>
            <Col md={12} className="mb-3">
              <Form.Control
                type="text"
                className="bg-light rounded-3"
                value={answers[q.id] || ""}
                onChange={(e) =>
                  setAnswers({
                    ...answers,
                    [q.id]: e.target.value,
                  })
                }
                
              />
            </Col>
          </Row>
        )}
        {q.type === "textarea" && (
          <Row>
            <Col md={12}>
              <Form.Control
                as="textarea"
                rows={4}
                className="bg-light rounded-3"
  
                value={answers[q.id] || ""}
                onChange={(e) =>
                  setAnswers({
                    ...answers,
                    [q.id]: e.target.value,
                  })
                }
              />
            </Col>
          </Row>
        )}
        {q.type === "radio" && (
          <div className="d-flex flex-column gap-2">
            {q.options.map((option, i) => (
              <Button
                key={i}
                variant={
                  answers[q.id] === option ? "subtle" : "light-solid"
                }
   
                className="d-flex gap-2 p-3"
                onClick={() => {
                  setAnswers((prevAnswers) => ({
                    ...prevAnswers,
                    [q.id]: option,
                  }));
                }}
              >
                <Form.Check
                  type="radio"
                  id={`radio-${q.id}-${i}`}
                  name={`q${q.id}`}
                  checked={answers[q.id] === option}
                  onChange={() => {}}
                  style={{ pointerEvents: "none" }}
                  className="me-2 mb-0"
                />
                <span className="text-nowrap">{option}</span>
              </Button>
            ))}
          </div>
        )}
        {q.type === "checkbox" && (
          <Row>
            <Col md={12} className="mb-3">
              {q.options.map((option, i) => (
                <Row key={i}>
                  <Col md={12} className="d-flex flex-column flex-start">
                    <Button
                      size="md"
                      variant={(answers[q.id] || []).includes(option) ? "subtle" : "light-solid"}
                      className="d-flex mb-2 gap-2 p-3"
                      onClick={() => {
                        const currentAnswers = answers[q.id] || [];
                        if (currentAnswers.includes(option)) {
                          setAnswers({
                            ...answers,
                            [q.id]: currentAnswers.filter((a) => a !== option),
                          });
                        } else {
                          setAnswers({
                            ...answers,
                            [q.id]: [...currentAnswers, option],
                          });
                        }
                      }}
                    >
                      <Form.Check
                        key={i}
                        type="checkbox"
                        id={`checkbox-${q.id}-${i}`}
                        label=""
                        value={option}
                        checked={(answers[q.id] || []).includes(option)}
                        onChange={(e) => e.stopPropagation()}
                        style={{ pointerEvents: "none" }}
                      />
                      {option}
                    </Button>
                  </Col>
                </Row>
              ))}
            </Col>
          </Row>
        )}
        {q.type === "dropdown" && (
          <Row>
            <Col md={6}>
              <Form.Select
                value={answers[q.id] || ""}
                onChange={(e) =>
                  setAnswers({
                    ...answers,
                    [q.id]: e.target.value,
                  })
                }
              >
                <option value="">Select an option</option>
                {q.options.map((option, i) => (
                  <option key={i} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        )}
        {q.type === "rating" && (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {getRatingOptions(q).map((opt) => (
              <Button
                key={String(opt.value)}
                variant={
                  String(answers[q.id]) === String(opt.value) ? "subtle" : "light-solid"
                }
                className="d-flex gap-3 p-3 w-100"
                onClick={() => {
                  setAnswers((prevAnswers) => ({
                    ...prevAnswers,
                    [q.id]: opt.value,
                  }));
                }}
              >
                <Form.Check
                  type="radio"
                  id={`rating-${q.id}-${String(opt.value)}`}
                  name={`rating-${q.id}`}
                  checked={String(answers[q.id]) === String(opt.value)}
                  onChange={() => {}}
                  style={{
                    pointerEvents: "none",
                    marginBottom: "0.25rem",
                  }}
                  className="m-0"
                />
                <span>{opt.label}</span>
              </Button>
            ))}
          </div>
        )}
        {q.type === "radio-matrix" && q.options.length > 0 && (
          <div className="table-responsive custom-modern-responsive custom-modern-responsive-hover">
            <Table>
              <thead>
                <tr>
                  <th className="px-3" style={{ width: "40%" }}>
                    Performance Factors
                  </th>
                  {(q.columns && q.columns.length > 0
                    ? q.columns
                    : [
                        "Unsatisfactory",
                        "Needs Improvement",
                        "Meets Expectations",
                        "Exceeds Expectations",
                      ]
                  ).map((col, colIdx) => (
                    <th key={colIdx}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.options.map((opt, idx) => (
                  <tr key={idx}>
                    <td className="fw-medium px-3">
                      {opt || `Factor ${idx + 1}`}
                    </td>
                    {(q.columns && q.columns.length > 0
                      ? q.columns
                      : [
                          "Unsatisfactory",
                          "Needs Improvement",
                          "Meets Expectations",
                          "Exceeds Expectations",
                        ]
                    ).map((_, colIdx) => (
                      <td key={colIdx} className="text-center">
                        <Form.Check
                          type="radio"
                          name={`matrix-${q.id}-${idx}`}
                          checked={answers[q.id]?.[idx] === colIdx}
                          onChange={() =>
                            setAnswers({
                              ...answers,
                              [q.id]: {
                                ...(answers[q.id] || {}),
                                [idx]: colIdx,
                              },
                            })
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {(q.showInfo || (q.infoHtml && String(q.infoHtml).trim())) && q.infoPlacement === "after" && (
          <div className="mt-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: q.infoHtml || "" }} />
        )}
      </Form.Group>
    );
  };

  if (!template) return <div>Loading...</div>;

  return (
    <div className="min-vh-100">
      <PageTitle title={template.title} description="Complete your evaluation form" />
      <Navbar pageTitle={template.title} />
      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
      <Row className="align-items-center">
                  <Col lg={template.isTimed ? 8 : 12} md={template.isTimed ? 6 : 12} className=" d-flex flex-row align-items-center justify-content-between gap-2">
                    <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                    <h5 className="fw-semibold mb-0 py-3">
                      {template.title}
                    </h5>
                    <span className={`badge small ${
                      template.evaluationType === 'performance' ? 'bg-primary' : 
                      template.evaluationType === 'feedback' ? 'bg-info' : 
                      template.evaluationType === 'survey' ? 'bg-success' :
                      template.evaluationType === 'questionnaire' ? 'bg-warning' :
                      'bg-secondary'
                    }`}>
                      {template.evaluationType === 'performance' ? 'Performance' : 
                       template.evaluationType === 'feedback' ? 'Feedback' : 
                       template.evaluationType === 'survey' ? 'Survey' :
                       template.evaluationType === 'questionnaire' ? 'Questionnaire' :
                       template.evaluationType || 'Evaluation'}
                    </span>
                    </div>

                  </Col>
                  {template.isTimed && (
                    <Col
                    lg={4}
                    md={6}
                    className="d-flex justify-content-md-end justify-content-center align-items-center mt-4 mt-md-0"
                  >
                    
                      <div className="d-flex align-items-center bg-lighter rounded-2 p-1 pb-2 px-3 border gap-3" style={{ lineHeight: 0.6 }}>
                        {(() => {
                          const totalMinutes = Math.floor(timeLeft / 60);
                          const hours = Math.floor(timeLeft / 3600);
                          const minutes = Math.floor((timeLeft % 3600) / 60);
                          const seconds = timeLeft % 60;
                          
                          const getColor = (value) => {
                            if (value === 0) return 'text-muted';
                            const timerColor = totalMinutes > 5 ? 'text-success' : (totalMinutes >= 1 ? 'text-danger' : 'text-danger');
                            return timerColor;
                          };
                          
                          return (
                            <>
                              <div className="d-flex flex-column align-items-center">
                                <h5 className={`fw-bold mb-0 ${getColor(hours)}`}>
                                  {hours.toString().padStart(2, "0")}
                                </h5>
                                <small className="text-muted" style={{ fontSize: '0.6rem' }}> Hrs</small>
                              </div>
                              <div className="d-flex flex-column align-items-center">
                                <h5 className={`fw-bold mb-0 ${getColor(minutes)}`}>
                                  {minutes.toString().padStart(2, "0")}
                                </h5>
                                <small className="text-muted" style={{ fontSize: '0.6rem' }}> Mins</small>
                              </div>
                              <div className="d-flex flex-column align-items-center">
                                <h5 className={`fw-bold mb-0 ${getColor(seconds)}`}>
                                  {seconds.toString().padStart(2, "0")}
                                </h5>
                                <small className="text-muted" style={{ fontSize: '0.6rem' }}> Secs</small>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                  </Col>)}
                </Row>
                </Container>
      </div>
      <Container className="py-4">
        <Row>
          {/* Sidebar for question list (hidden on xs/sm) */}
          <Col lg={3} md={4} className="d-none d-md-block">
            
              <Card className="mb-3 border-0 shadow-sm">
                <Card.Header
                  className="fw-bold border-0 px-4 pt-3 mb-2"
                >
                  Progress
                </Card.Header>
                <Card.Body className="px-3 pt-0">
                  <div className="d-flex flex-column justify-content-between align-items-center gap-2">
                  <div className="flex-grow-1 w-100">
                    <ProgressBar
                      now={progressPercent}
                      label=""
                      style={{
                        width: "100%",
                        height: 6,
                        fontWeight: 600,
                        fontSize: 15,
                      }}
                      variant={
                        progressPercent === 100 ? "bs-success" : "bs-primary"
                      }
                      animated
                    />
                  </div>
                   <div className="d-flex flex-row align-items-center justify-content-between w-100 gap-2">
                      <span className="flex-grow-1 small fw-semibold">
                        {progressPercent}% complete
                      </span>
                      <span className="text-muted small text-end">
                      {filledQuestions}/{totalQuestions || 0} answered
                    </span>
                  </div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm">
                <Card.Header
                  className="fw-bold border-0 px-4 pt-3 pb-2 mb-2"
                >
                  Questions
                </Card.Header>
                <Card.Body className="px-3 pt-0">
                  <ul className="list-unstyled mb-0">
                    {navigationEntries.map((entry, idx) => {
                      const isActive = idx === activeEntryIndex;
                      const isQuestionEntry = entry.type === "question";
                      const sectionMeta =
                        typeof entry.sectionIndex === "number"
                          ? sections[entry.sectionIndex]
                          : null;
                      let label;
                      if (entry.type === "intro") {
                        label = "Introduction";
                      } else if (isQuestionEntry) {
                        label =
                          entry.question.label ||
                          entry.question.questionText ||
                          `Question ${entry.questionNumber}`;
                      } else {
                        label =
                          sectionMeta?.header ||
                          `Section ${typeof entry.sectionIndex === "number" ? entry.sectionIndex + 1 : ""}`;
                      }
                      return (
                        <li key={entry.id || idx} className="mb-2">
                          <Button
                            variant="light"
                            className="d-flex align-items-center justify-content-between p-2 rounded-3"
                            style={{ overflow: "hidden", width: "100%" }}
                            onClick={() => setActiveEntryIndex(idx)}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className={`rounded-circle d-flex align-items-center justify-content-center ${
                                  isQuestionEntry && isQuestionFilled(entry.question)
                                    ? "bg-success text-white"
                                    : "border text-muted"
                                }`}
                                style={{ width: 32, height: 32 }}
                              >
                                {isQuestionEntry ? (
                                  isQuestionFilled(entry.question) ? (
                                    <i className="bi bi-check" />
                                  ) : (
                                    <span className="fw-semibold">
                                      {entry.questionNumber}
                                    </span>
                                  )
                                ) : (
                                  <i className="bi bi-info-lg text-muted" />
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className="fw-semibold text-truncate small"
                                  style={{ maxWidth: "160px" }}
                                >
                                  {label}
                                </div>
                                {isQuestionEntry && bookmarks.has(entry.question.id) && (
                                  <i className="bi bi-bookmark-fill text-danger" style={{ fontSize: '0.875rem' }}></i>
                                )}
                              </div>
                            </div>
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </Card.Body>
              </Card>
          </Col>
          {/* Main content */}
          <Col lg={9} md={8}>
            <Card className="mb-4 shadow-sm border-0 rounded-4 " style={{ height: `calc(100%)` }}>
              <Card.Body className="px-4 py-4">
                <Form>
                  {isIntroActive ? (
                    template?.introHtml ? (
                      <div
                        className="mb-4 p-3 rounded-3 bg-light border-0 eval-rich-content"
                        style={{ lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: template.introHtml }}
                      />
                    ) : (
                      <div className="text-muted py-4">
                        No introduction provided for this evaluation.
                      </div>
                    )
                  ) : isOverviewActive ? (
                    <div>
                      <h4 className="fw-bold mb-2">{activeSectionTitle}</h4>
                      {activeSectionParagraph && (
                        <p className="text-muted mb-0">{activeSectionParagraph}</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      {currentQuestion ? (
                        renderQuestion(currentQuestion, displayQuestionNumber)
                      ) : (
                        <div className="text-muted py-4">
                          No question selected.
                        </div>
                      )}
                    </div>
                  )}

                </Form>
              </Card.Body>
              <Card.Footer
                className="d-flex  justify-content-between align-items-md-center gap-3 p-4"
                style={{
                  borderTop: "1px dashed var(--border-bg)",
                  backgroundColor: "var(--bs-card-bg)",
                }}
              >
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={handleSaveProgressAndNavigate}
                  >
                   <i className="bi bi-floppy2"></i>
                  </Button>
                  {/*
                  <Button variant="dark" onClick={handleSubmit}>
                    Submit Evaluation
                  </Button>
                  */}
                </div>                 
                 <div className="d-flex justify-content-between align-items-center gap-3">
                    {/*<div className="text-muted">
                      {isOverviewActive
                        ? "Section overview"
                        : hasQuestions
                        ? `Question ${displayQuestionNumber} of ${totalQuestions}`
                        : "No questions available"}
                    </div>*/}
                    <div className="d-flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handlePreviousEntry}
                        disabled={activeEntryIndex === 0}
                      >
                        <i className="bi bi-arrow-left b-block d-md-none"></i> <span className="d-none d-md-inline">Previous</span>
                      </Button>
                      <Button
                        variant={allQuestionsAnswered ? "success" : "dark"}
                        size="sm"
                        onClick={
                          allQuestionsAnswered ? handleSubmit : handleNextEntry
                        }
                        disabled={
                          allQuestionsAnswered
                            ? false
                            : totalEntries === 0 ||
                              activeEntryIndex === totalEntries - 1
                        }
                      >
                        {allQuestionsAnswered ? "Submit Evaluation" : "Continue"}
                      </Button>
                    </div>
                  </div>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Navigation Warning Modal */}
      <NavigationWarningModal
        show={showWarningModal}
        onHide={handleCancelNavigation}
        onSaveAndLeave={handleSaveAndLeave}
        onLeaveWithoutSaving={handleLeaveWithoutSaving}
        isTimed={isTimed}
        timeLeft={warningTimeLeft}
      />
    </div>
  );
};

export default ResidentEvaluationForm;
